import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckSquare, Plus, Users, Globe, MapPin, Briefcase, ListChecks } from 'lucide-react';
import { UnifiedTodoModal } from '@/components/crm/UnifiedTodoModal';
import { useToast } from '@/hooks/use-toast';
import {
  UnifiedTodo,
  sortTodosByDependency,
} from '@/lib/todoHelpers';
import { TodoFilters, TodoFiltersState, defaultFilters, applyTodoFilters } from '@/components/todos/TodoFilters';
import { TodoBulkActions } from '@/components/todos/TodoBulkActions';
import { TodoCard } from '@/components/todos/TodoCard';

export default function ChargingTodos() {
  const { user } = useAuth();
  const [selectedTodo, setSelectedTodo] = useState<UnifiedTodo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState<TodoFiltersState>(defaultFilters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: todos = [], isLoading, isError } = useQuery({
    queryKey: ['charging-unified-todos', user?.id, filters.showCompleted],
    queryFn: async () => {
      console.log('[ChargingTodos] Fetching todos for charging user:', user?.id);
      
      let query = supabase
        .from('unified_todos' as any)
        .select(`
          *,
          charging_locations(id, name, city, stage),
          charging_opportunities(id, name),
          profiles:assigned_to(id, display_name, email),
          parent_todo:depends_on_todo_id(id, title, status)
        `)
        .is('deleted_at', null)
        .is('project_id', null) // Charging users can't see project todos
        .order('created_at', { ascending: false });

      if (!filters.showCompleted) {
        query = query.neq('status', 'completed');
      }

      const { data, error } = await query;

      if (error) {
        console.error('[ChargingTodos] Query error:', error);
        throw error;
      }
      
      console.log('[ChargingTodos] Fetched', data?.length || 0, 'todos');
      return (data || []) as any as UnifiedTodo[];
    },
    enabled: !!user?.id,
    retry: 1,
  });

  const handleToggleComplete = async (todoId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'open' : 'completed';
    const { error } = await supabase
      .from('unified_todos' as any)
      .update({
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
      })
      .eq('id', todoId);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['charging-unified-todos'] });
      toast({
        title: 'Succes',
        description: `Taak ${newStatus === 'completed' ? 'afgerond' : 'heropend'}`,
      });
    } else {
      toast({
        title: 'Fout',
        description: 'Fout bij bijwerken taak',
        variant: 'destructive',
      });
    }
  };

  const handleSelectTodo = (id: string, selected: boolean) => {
    setSelectedIds(prev => 
      selected ? [...prev, id] : prev.filter(i => i !== id)
    );
  };

  // Apply filters
  const filteredTodos = useMemo(() => {
    return applyTodoFilters(todos, filters);
  }, [todos, filters]);

  // Group by scope
  const scopeGroups = useMemo(() => {
    const personal = filteredTodos.filter(t => t.scope === 'personal_charging');
    const teamCharging = filteredTodos.filter(t => t.scope === 'team_charging');
    const teamAll = filteredTodos.filter(t => t.scope === 'team_all');
    
    // Context-based
    const locationTodos = filteredTodos.filter(t => t.scope === 'location');
    const opportunityTodos = filteredTodos.filter(t => t.scope === 'opportunity');

    // Group locations
    const locationsGrouped = locationTodos.reduce((acc: Record<string, UnifiedTodo[]>, todo) => {
      const key = todo.charging_locations?.name || 'Onbekende locatie';
      if (!acc[key]) acc[key] = [];
      acc[key].push(todo);
      return acc;
    }, {});

    // Group opportunities
    const opportunitiesGrouped = opportunityTodos.reduce((acc: Record<string, UnifiedTodo[]>, todo) => {
      const key = todo.charging_opportunities?.name || 'Onbekende opportunity';
      if (!acc[key]) acc[key] = [];
      acc[key].push(todo);
      return acc;
    }, {});

    return {
      personal: sortTodosByDependency(personal),
      teamCharging: sortTodosByDependency(teamCharging),
      teamAll: sortTodosByDependency(teamAll),
      locations: locationsGrouped,
      opportunities: opportunitiesGrouped,
    };
  }, [filteredTodos]);

  const totalCount = filteredTodos.length;
  const hasAnyTodos = totalCount > 0;

  const renderTodoList = (todoList: UnifiedTodo[], showContext = false) => (
    <div className="space-y-2">
      {todoList.map((todo) => (
        <TodoCard
          key={todo.id}
          todo={todo}
          allTodos={filteredTodos}
          onToggleComplete={handleToggleComplete}
          onEdit={(t) => {
            setSelectedTodo(t);
            setIsModalOpen(true);
          }}
          onSelect={handleSelectTodo}
          isSelected={selectedIds.includes(todo.id)}
          selectionMode={selectionMode}
          showContext={showContext}
        />
      ))}
    </div>
  );

  const ScopeSection = ({ 
    title, 
    icon: Icon, 
    todos: sectionTodos, 
    showContext = false,
    emptyText = 'Geen taken'
  }: { 
    title: string; 
    icon: any; 
    todos: UnifiedTodo[];
    showContext?: boolean;
    emptyText?: string;
  }) => (
    <Card className="p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
        <span className="text-muted-foreground text-sm font-normal">({sectionTodos.length})</span>
      </h3>
      {sectionTodos.length > 0 ? (
        renderTodoList(sectionTodos, showContext)
      ) : (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      )}
    </Card>
  );

  const ContextSection = ({
    title,
    icon: Icon,
    groups,
  }: {
    title: string;
    icon: any;
    groups: Record<string, UnifiedTodo[]>;
  }) => {
    const entries = Object.entries(groups);
    if (entries.length === 0) return null;

    return (
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {title}
        </h3>
        <div className="space-y-4">
          {entries.map(([name, groupTodos]) => (
            <div key={name}>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">{name}</h4>
              {renderTodoList(sortTodosByDependency(groupTodos))}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <CheckSquare className="w-8 h-8" />
              Mijn Taken
            </h1>
            <p className="text-muted-foreground">
              {totalCount} {totalCount === 1 ? 'taak' : 'taken'} gevonden
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={selectionMode ? "secondary" : "outline"}
              onClick={() => {
                if (selectionMode) {
                  setSelectedIds([]);
                }
                setSelectionMode(!selectionMode);
              }}
            >
              <ListChecks className="w-4 h-4 mr-2" />
              {selectionMode ? 'Annuleren' : 'Selecteren'}
            </Button>
            <Button
              onClick={() => {
                setSelectedTodo(null);
                setIsModalOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe taak
            </Button>
          </div>
        </div>

        {/* Filters */}
        <TodoFilters filters={filters} onFiltersChange={setFilters} />

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-8">Laden...</div>
        ) : isError ? (
          <div className="text-center py-8 text-destructive">Fout bij laden van taken</div>
        ) : !hasAnyTodos ? (
          <Card className="p-8 text-center">
            <CheckSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Geen taken gevonden</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Scope-based sections */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ScopeSection 
                title="Mijn Taken" 
                icon={CheckSquare} 
                todos={scopeGroups.personal}
                emptyText="Geen persoonlijke taken"
              />
              <ScopeSection 
                title="Charging Team" 
                icon={Users} 
                todos={scopeGroups.teamCharging}
                emptyText="Geen team taken"
              />
              <ScopeSection 
                title="Gezamenlijk" 
                icon={Globe} 
                todos={scopeGroups.teamAll}
                emptyText="Geen gezamenlijke taken"
              />
            </div>

            {/* Context-based sections */}
            <ContextSection
              title="Locatie Taken"
              icon={MapPin}
              groups={scopeGroups.locations}
            />
            <ContextSection
              title="Opportunity Taken"
              icon={Briefcase}
              groups={scopeGroups.opportunities}
            />
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectionMode && selectedIds.length > 0 && (
        <TodoBulkActions
          selectedIds={selectedIds}
          onClearSelection={() => setSelectedIds([])}
          onActionComplete={() => {
            setSelectedIds([]);
            queryClient.invalidateQueries({ queryKey: ['charging-unified-todos'] });
          }}
          onExitSelectionMode={() => setSelectionMode(false)}
        />
      )}

      <UnifiedTodoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTodo(null);
        }}
        todo={selectedTodo}
        onSave={async () => {
          await queryClient.invalidateQueries({ queryKey: ['charging-unified-todos'] });
        }}
      />
    </div>
  );
}
